<template>
  <div>
    <div class="mb-4"
         :style="placeholderStyle"
         ref="placeholder"/>
    <div class="carousel file-carousel slide"
         :class="{'mb-4': !isBeforeOpening && !isOpening, 'before-opening': isBeforeOpening, 'opening': isOpening, 'before-closing': isBeforeClosing, 'closing': isClosing}"
         :style="carouselStyle"
         ref="carousel"
         data-bs-ride="false"
         data-bs-keyboard="false"
         data-bs-touch="false">
      <div v-if="value.length > 1" :style="indicatorsStyle" class="carousel-indicators">
        <button v-for="(file, index) in value"
                :key="'indicator' + index"
                type="button"
                @click.prevent="gotoIndex(index)"
                :class="{'active': target != null ? index == target : index == active}"
                :aria-current="target != null ? index == target : index == active"
                :aria-label="'Slide ' + (index + 1)"></button>
      </div>
      <div class="carousel-inner">
        <div v-for="(file, index) in value"
             :key="'file' + index"
             @click.prevent="isOpening ? close() : open()"
             class="carousel-item align-items-center"
             :class="itemClass(index)">
          <figure ref="figure">
            <a :href="window.fileDomain + '/' + file.code + '.' + file.extension"
               :title="file.seoText"
               target="_blank"
               @click.prevent=""
               ref="link"
               :style="'background-image: url(' + window.fileDomain + '/ajax-loader/transparent-black-16x16.svg);'">
              <img :src="window.fileDomain + '/' + file.code + '_1000_1000.' + file.extension"
                   @load="setTextHeight(index)"
                   :style="imgStyle"/>
            </a>
            <figcaption v-if="file.seoText">{{ file.seoText }}</figcaption>
          </figure>
        </div>
      </div>
      <button v-show="isOpening" type="button" @click.prevent="close()" class="btn btn-close">
        <i class="fa-solid fa-times"></i>
      </button>
      <button v-if="value.length > 1" type="button" :style="controlsStyle" class="carousel-control-prev" @click.prevent="gotoPrev()">
        <i class="fa-solid fa-chevron-left"></i>
      </button>
      <button v-if="value.length > 1" type="button" :style="controlsStyle" class="carousel-control-next" @click.prevent="gotoNext()">
        <i class="fa-solid fa-chevron-right"></i>
      </button>
    </div>
    <div v-if="value[active] && value[active].description" v-html="value[active].description"
         style="font-size: 0.8125rem;" class="mb-4"/>
  </div>
</template>

<script>
export default {
  name: "file-carousel",
  props: {
    value: {
      type: Array
    }
  },
  data: function () {
    return {
      active: 0,
      target: null,
      animate: false,
      goingPrev: false,
      goingNext: false,
      isBeforeOpening: false,
      isOpening: false,
      isBeforeClosing: false,
      isClosing: false,
      left: null,
      top: null,
      width: null,
      height: null,
      hash: "open-" + new Date().getTime(),
      timeouts: [],
      textHeight: 0,
      windowHeight: window.innerHeight,
      transitionSpeed: 250
    };
  },
  mounted: function () {
    window.addEventListener("resize", this.handleResize);
  },
  created: function () {
    document.addEventListener("keydown", this.keydownListener);
  },
  beforeDestroy: function () {
    window.removeEventListener("resize", this.handleResize);
  },
  destroyed: function () {
    document.removeEventListener("keydown", this.keydownListener);
  },
  computed: {
    carouselStyle: function () {
      var style = "";
      if (this.isBeforeOpening || this.isClosing) {
        if (this.left != null) {
          style += "left: " + this.left + "px;";
        }
        if (this.top != null) {
          style += "top: " + this.top + "px;";
        }
      }
      if (this.width != null) {
        style += "width: " + this.width + "px;";
      }
      if (this.height != null) {
        style += "height: " + this.height + "px;";
      }
      return style;
    },
    placeholderStyle: function () {
      var style = "";
      if (this.isBeforeOpening || this.isOpening || this.isBeforeClosing || this.isClosing) {
        if (this.width != null) {
          style += "width: " + this.width + "px;";
        }
        if (this.height != null) {
          style += "height: " + this.height + "px;";
        }
      } else {
        style += "display: none;";
      }
      return style;
    },
    imgStyle: function () {
      var style = "";
      if (this.isBeforeOpening || this.isOpening) {
        // The "45" subtracted here is for the carousel-indicators and some margin above the image and below the text.
        // Need to double this, since the image is vertically centered, so the margin is equally distributed above
        // and below the image.
        const maxHeight = this.windowHeight - (2 * (this.textHeight + 45));
        style += "max-height: " + maxHeight + "px;";
      }
      return style;
    },
    indicatorsStyle: function () {
      if (this.isOpening) {
        return "";
      }
      return "bottom: " + this.textHeight + "px;";
    },
    controlsStyle: function () {
      if (this.isOpening) {
        return "";
      }
      return "bottom: " + this.textHeight + "px;";
    }
  },
  watch: {
    active: function (newValue) {
      this.setTextHeight(newValue);
    }
  },
  methods: {
    handleResize: function () {
      this.windowHeight = window.innerHeight;
    },
    getElementHeight: function (elements, index) {
      if (elements && elements.length >= index) {
        const element = elements[index];
        const rect = element.getBoundingClientRect();
        if (rect) {
          return rect.height;
        }
      }
      return 0;
    },
    setTextHeight: function (index) {
      if (this.active !== index) {
        return;
      }
      const figureHeight = this.getElementHeight(this.$refs.figure, index);
      const linkHeight = this.getElementHeight(this.$refs.link, index);
      this.textHeight = figureHeight - linkHeight;
    },
    keydownListener: function (event) {
      if (event.keyCode == 27) {
        this.close();
      } else if (event.keyCode == 37) {
        this.gotoPrev();
      } else if (event.keyCode == 39) {
        this.gotoNext();
      }
    },
    itemClass: function (index) {
      return {
        "active": this.active == index,
        "carousel-item-prev": this.goingPrev && this.target == index, // On target going back
        "carousel-item-end": this.goingPrev && this.animate && [this.target, this.active].indexOf(index) != -1, // On target and source going back
        "carousel-item-next": this.goingNext && this.target == index, // On target going forward
        "carousel-item-start": this.goingNext && this.animate && [this.target, this.active].indexOf(index) != -1 // On target and source going forward
      };
    },
    gotoPrev: function () {
      this.gotoIndex(this.active - 1);
    },
    gotoNext: function () {
      this.gotoIndex(this.active + 1);
    },
    gotoIndex: function (index) {
      if (this.active == index || this.target != null) {
        return;
      }
      this.goingPrev = index < this.active;
      this.goingNext = index > this.active;
      if (index < 0) {
        index = this.value.length - 1;
      } else if (index > this.value.length - 1) {
        index = 0;
      }
      this.target = index;
      this.$nextTick(() => {
        setTimeout(() => {
          this.animate = true;
          setTimeout(() => {
            this.active = this.target;
            this.target = null;
            this.animate = false;
          }, this.transitionSpeed);
        }, 0);
      });
    },
    resetTargetSize: function () {
      this.left = null;
      this.top = null;
      this.width = null;
      this.height = null;
    },
    setTargetSize: function (rect, force) {
      try {
        if (this.left == null || force) {
          this.left = rect.left;
        }
        if (this.top == null || force) {
          this.top = rect.top;
        }
        if (this.width == null || force) {
          this.width = rect.width;
        }
        if (this.height == null || force) {
          this.height = rect.height;
        }
      } catch (err) {
        console.log(err);
        this.resetTargetSize();
      }
    },
    open: function () {
      this.clearTimeouts();
      this.isBeforeClosing = false;
      this.isClosing = false;
      this.setTargetSize(this.$refs.carousel.getBoundingClientRect());
      location.hash = this.hash;
      this.isBeforeOpening = true;
      this.isOpening = false;
      this.$nextTick(() => {
        this.timeouts.push(
            setTimeout(() => {
              this.isBeforeOpening = false;
              this.isOpening = true;
            }, 0)
        );
      });
      window.addEventListener("popstate", this.close);
    },
    close: function () {
      // Do not allow to close unless it is open.
      if (!this.isBeforeOpening && !this.isOpening) {
        return;
      }
      this.clearTimeouts();
      this.isBeforeOpening = false;
      this.isOpening = false;
      this.setTargetSize(this.$refs.placeholder.getBoundingClientRect(), true);
      this.isBeforeClosing = true;
      this.isClosing = false;
      this.$nextTick(() => {
        this.timeouts.push(
            setTimeout(() => {
              this.isBeforeClosing = false;
              this.isClosing = true;
              this.$nextTick(() => {
                this.timeouts.push(
                    setTimeout(() => {
                      this.isClosing = false;
                      this.setTextHeight(this.active);
                      this.resetTargetSize();
                    }, this.transitionSpeed)
                );
              });
            }, 0)
        );
      });
      window.removeEventListener("popstate", this.close);
    },
    clearTimeouts: function () {
      var timeouts = this.timeouts.splice(0, this.timeouts.length);
      for (var i = 0; i < timeouts.length; i++) {
        clearTimeout(timeouts[i]);
      }
    }
  }
}
</script>

<style lang="less">
.carousel-transition {
  transition: all .25s ease;
}
.carousel.file-carousel {
  .carousel-transition;

  .carousel-inner {
    .carousel-transition;
    background-color: rgba(0, 0, 0, 0);
    width: 100%;
    height: 100%;
    padding: 0;
  }

  .carousel-item {
    .carousel-transition;
    width: 100%;

    & > figure {
      margin: 0;
      padding: 0;
      width: 100%;

      & > a {
        background-position: center;
        background-repeat: no-repeat;
        display: block;
        width: 100%;

        & > img {
          .carousel-transition;
          width: 100%;
        }
      }

      & > figcaption {
        .carousel-transition;
        color: inherit;
        font-size: .85rem;
        margin: .125rem 0 0 0;
        padding: 0;
      }
    }
  }

  &.before-opening, &.opening, &.before-closing, &.closing {
    position: fixed;
    z-index: 2000;
  }

  &.opening, &.before-closing, &.closing {
    left: 0;
    top: 0;

    .carousel-item {
      cursor: zoom-out;
      height: 100%;
      width: 100%;

      & > figure {
        display: table;
        margin: auto;
        width: auto;

        & > a {
          cursor: zoom-out;

          & > img {
            box-shadow: 0 .125rem .25rem rgba(0, 0, 0, .15);
            max-width: 100%;
            max-height: 100%;
            width: auto;
          }
        }

        & > figcaption {
          display: table-caption;
          caption-side: bottom;
        }
      }
    }
  }

  &.opening {
    height: 100vh !important;
    width: 100% !important;

    .carousel-inner {
      background-color: rgba(0, 0, 0, .75);
      padding: 1rem;
    }

    .carousel-item {
      & > figure {
        & > figcaption {
          color: #fff;
        }
      }
    }
  }

  // Need "flex" to keep things vertical aligned with "align-items-center". Both while active and while going to the
  // previous or next in the carousel.
  .carousel-item-prev, .carousel-item-next, .carousel-item.active {
    display: flex;
  }

  .carousel-control-prev, .carousel-control-next {
    .carousel-transition;
    color: #fff;
    font-size: 2rem;
    opacity: 1;
    text-shadow: 0 0 .25rem rgba(0, 0, 0, 1);

    &:focus, &:hover {
      opacity: 1;
      transform: scale(1.1);
    }
  }

  .carousel-indicators {
    .carousel-transition;

    & button {
      box-sizing: content-box;
      flex: 0 1 auto;
      width: 30px;
      height: 5px;
      padding: 0;
      margin: 0 3px 1.5rem 3px;
      text-indent: -999px;
      cursor: pointer;
      background-color: #fff;
      background-clip: padding-box;
      border: 0;
      opacity: .5;
      box-shadow: 0 0 .25rem rgba(0, 0, 0, 1);

      &:focus, &:hover {
        opacity: 1;
        transform: scale(1.1);
      }
    }

    .active {
      opacity: 1;
    }
  }

  .btn-close {
    top: 0;
    right: 0;
    position: absolute;
    font-size: 2.5rem;
    border: 0;
    background: transparent;
    color: #fff;
    z-index: 2;
    outline: 0;
    line-height: 0;
    opacity: 1;
    text-shadow: 0 0 .25rem rgba(0, 0, 0, 1);

    &:focus, &:hover {
      opacity: 1;
      transform: scale(1.1);
    }
  }
}
</style>